/**
 * The deterministic sync-progress label the release-safe {@link SyncStatusSentinel} publishes to
 * the accessibility tree.
 *
 * `initialPullGeneration` is a monotonic counter bumped once each time the actor DB's initial
 * remote pull drains to completion (see `lib/sync-client/use-sync-status.ts`). It fires exactly
 * once per completed drain, so `actor-synced gen N` is the clean "the synced deck has fully
 * landed locally" edge an E2E (Maestro) flow blocks on — instead of racing the sync by polling
 * the UI for a specific synced row. Before the first drain (generation 0) it reads
 * `actor-syncing`.
 */
/**
 * Diagnostic companion to {@link getSyncAccessibilityLabel}: the sync lifecycle phase, local
 * replica row count and last recorded failure. Release builds log only to Sentry, so this is what
 * makes "the sentinel never left `actor-syncing`" diagnosable from a Maestro hierarchy dump.
 *
 * `syncPush` adds the silent sync push state (`lib/sync-push`): whether this install is registered,
 * and how many sync pushes it received / caught up on (`push=received/caught-up`). The sync push
 * Maestro flow waits on it.
 */
export function getSyncDiagnosticsAccessibilityLabel(
  status: {
    failure: { kind: string; message: string } | null;
    phase: string;
    syncedEntryCount: number | null;
  },
  syncPush?: { caughtUpSeq: number; isRegistered: boolean; signalSeq: number },
): string {
  const failure =
    status.failure == null ? "none" : `${status.failure.kind}: ${status.failure.message}`;
  const push =
    syncPush === undefined
      ? ""
      : ` push=${syncPush.signalSeq}/${syncPush.caughtUpSeq} push-registered=${syncPush.isRegistered ? "yes" : "no"}`;
  return `actor-sync-diag phase=${status.phase} entries=${status.syncedEntryCount ?? "?"} failure=${failure}${push}`;
}

export function getSyncAccessibilityLabel(generation: number): string {
  return generation === 0 ? "actor-syncing" : `actor-synced gen ${generation}`;
}
